import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import { FaSpinner } from 'react-icons/fa/index.js';
import callOysterApi from '../../utils/call-oyster-api.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import '../../styles/EditUserModal.css';

const EditUserModal = ({ isOpen, onClose, user, onUserUpdated }) => {
    const { user: currentUserData } = useCurrentUser();
    const canAssignLead =
        currentUserData?.isItLeadership || currentUserData?.isTeamLead;
    const canAssignAdmin = currentUserData?.isItLeadership;
    const canChangeTeam = currentUserData?.isItLeadership;
    const canGrantExecutivePulse = currentUserData?.isItLeadership;

    // Prevent editing roles of users with equal or higher permissions,
    // and prevent users from editing their own role (self-demotion)
    const isSelf = user?.email === currentUserData?.email;
    const targetIsHigherOrEqual =
        user?.isItLeadership ||
        (user?.isTeamLead && !currentUserData?.isItLeadership);
    const canEditRole = !targetIsHigherOrEqual && !isSelf;

    const [formData, setFormData] = useState({
        hdTeam: '',
        shift: 'Sundancers',
        role: 'user', // 'user', 'lead', 'admin'
        executivePulseAccess: false
    });
    const [teams, setTeams] = useState([]);
    const [saving, setSaving] = useState(false);

    const fetchTeams = useCallback(async () => {
        try {
            const response = await callOysterApi('/teams', 'GET');
            if (response.ok) {
                const data = await response.json();
                if (data.success && Array.isArray(data.payload)) {
                    setTeams(data.payload);
                }
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    }, []);

    useEffect(() => {
        if (isOpen && user) {
            // Determine initial role
            let role = 'user';
            if (user.isItLeadership) role = 'admin';
            else if (user.isTeamLead) role = 'lead';

            setFormData({
                hdTeam: user.hdTeam || '',
                shift: user.shift || '',
                role,
                executivePulseAccess: !!user.executivePulseAccess
            });
            fetchTeams();
        }
    }, [isOpen, user, fetchTeams]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'hdTeam') {
            setFormData((prev) => ({ ...prev, [name]: value, shift: '' }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);

            // Map role back to boolean flags
            const updates = {
                ...user, // Keep existing fields
                hdTeam: formData.hdTeam,
                shift: formData.shift,
                isItLeadership: formData.role === 'admin',
                isTeamLead: formData.role === 'lead',
                executivePulseAccess: !!formData.executivePulseAccess
            };

            // Explicitly set false if not selected to clear permissions
            if (formData.role === 'user') {
                updates.isItLeadership = false;
                updates.isTeamLead = false;
            } else if (formData.role === 'lead') {
                updates.isItLeadership = false;
                updates.isTeamLead = true;
            } else if (formData.role === 'admin') {
                updates.isItLeadership = true;
            }

            if (!canGrantExecutivePulse) {
                updates.executivePulseAccess = !!user.executivePulseAccess;
            }

            const response = await callOysterApi('/users/edit', 'PUT', updates);

            if (response.ok) {
                toast.success('User updated successfully');
                if (onUserUpdated) onUserUpdated();
                onClose();
            } else {
                toast.error('Failed to update user');
            }
        } catch (error) {
            console.error('Error updating user:', error);
            toast.error('Error updating user');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !user) return null;

    return (
        <div className="edit-user-modal-overlay">
            <div className="modal-content edit-user-modal">
                <div className="modal-header">
                    <h2>
                        Edit User: {user.firstName} {user.lastName}
                    </h2>
                    <button className="close-button" onClick={onClose}>
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="edit-user-form">
                    <div className="form-group">
                        <label>Team</label>
                        <select
                            name="hdTeam"
                            value={formData.hdTeam}
                            onChange={handleChange}
                            className="form-select">
                            {canChangeTeam ? (
                                <>
                                    <option value="">No Team</option>
                                    {teams.map((team) => (
                                        <option
                                            key={team._id || team.name || team}
                                            value={team.name || team}>
                                            {team.name || team}
                                        </option>
                                    ))}
                                </>
                            ) : (
                                <>
                                    <option value="">No Team</option>
                                    {currentUserData?.hdTeam && (
                                        <option value={currentUserData.hdTeam}>
                                            {currentUserData.hdTeam}
                                        </option>
                                    )}
                                </>
                            )}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Shift</label>
                        <select
                            name="shift"
                            value={formData.shift || ''}
                            onChange={handleChange}
                            className="form-select">
                            <option value="">No Shift</option>
                            {(
                                teams.find(
                                    (t) => (t.name || t) === formData.hdTeam
                                )?.shifts || [
                                    'Sundancers',
                                    'Moonwalkers',
                                    'Midknight Crawlers'
                                ]
                            ).map((shift) => (
                                <option key={shift} value={shift}>
                                    {shift}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Role</label>
                        {!canEditRole && (
                            <p
                                className="role-locked-hint"
                                style={{
                                    fontSize: '0.8rem',
                                    color: '#9ca3af',
                                    margin: '0 0 0.5rem 0',
                                    fontStyle: 'italic'
                                }}>
                                {isSelf
                                    ? 'You cannot change your own role.'
                                    : 'You cannot change the role of a user with equal or higher permissions.'}
                            </p>
                        )}
                        <div className="radio-group">
                            <label
                                style={{
                                    opacity: !canEditRole ? 0.5 : 1,
                                    cursor: !canEditRole
                                        ? 'not-allowed'
                                        : 'pointer'
                                }}>
                                <input
                                    type="radio"
                                    name="role"
                                    value="user"
                                    checked={formData.role === 'user'}
                                    onChange={handleChange}
                                    disabled={!canEditRole}
                                />
                                Standard User
                            </label>
                            <label
                                style={{
                                    opacity:
                                        !canAssignLead || !canEditRole
                                            ? 0.5
                                            : 1,
                                    cursor:
                                        !canAssignLead || !canEditRole
                                            ? 'not-allowed'
                                            : 'pointer'
                                }}>
                                <input
                                    type="radio"
                                    name="role"
                                    value="lead"
                                    checked={formData.role === 'lead'}
                                    onChange={handleChange}
                                    disabled={!canAssignLead || !canEditRole}
                                />
                                Team Lead
                            </label>
                            <label
                                style={{
                                    opacity:
                                        !canAssignAdmin || !canEditRole
                                            ? 0.5
                                            : 1,
                                    cursor:
                                        !canAssignAdmin || !canEditRole
                                            ? 'not-allowed'
                                            : 'pointer'
                                }}>
                                <input
                                    type="radio"
                                    name="role"
                                    value="admin"
                                    checked={formData.role === 'admin'}
                                    onChange={handleChange}
                                    disabled={!canAssignAdmin || !canEditRole}
                                />
                                Leadership (Admin)
                            </label>
                        </div>
                    </div>

                    {canGrantExecutivePulse && (
                        <div className="form-group">
                            <label
                                className="checkbox-label"
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '0.5rem',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                }}>
                                <input
                                    type="checkbox"
                                    checked={formData.executivePulseAccess}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            executivePulseAccess:
                                                e.target.checked
                                        }))
                                    }
                                    style={{ marginTop: '0.2rem' }}
                                />
                                <span>Executive Pulse dashboard access</span>
                            </label>
                            <p
                                style={{
                                    fontSize: '0.85rem',
                                    color: '#6b7280',
                                    margin: '0.35rem 0 0 1.35rem'
                                }}>
                                Enables the Executive Pulse OKR dashboard in
                                Oracle for this user&apos;s account.
                            </p>
                        </div>
                    )}

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-save"
                            disabled={saving}>
                            {saving ? (
                                <>
                                    <FaSpinner className="spinner" />{' '}
                                    {'Saving...'}
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

EditUserModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onUserUpdated: PropTypes.func,
    user: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
        isItLeadership: PropTypes.bool,
        isTeamLead: PropTypes.bool,
        hdTeam: PropTypes.string,
        shift: PropTypes.string,
        executivePulseAccess: PropTypes.bool
    })
};

export default EditUserModal;
